import React, { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import api from '../api/axiosConfig';
import { apiErrorText } from '../utils/apiErrors';
import { useAuth } from '../context/AuthContext';
import { hasNavPermission } from '../components/ProtectedRoute';
import { formatISODate } from '../utils/dates';
import { isoYearOfDate, useHistoryFilters } from '../components/HistoryFilters';
import HistorySummary from '../components/HistorySummary';
import { formatBoxes43, formatRatio, summarizeHarvestSales } from '../utils/harvestSales';
import { exportHarvestToExcel, exportHarvestToPDF, harvestSheet } from '../utils/harvestSheetExport';
import ExportPreviewModal from '../components/ExportPreviewModal';
import RowProperties from '../components/RowProperties';
import SheetPreview from '../components/SheetPreview';
import './CompaniesPage.css';
import './SalesFormPage.css';
import '../styles/rowActions.css';
import '../styles/topbarButtons.css';
import '../styles/pageHeader.css';

// How the shared Historial filters read a harvest sheet.
const FILTER_FIELDS = {
  year: (h) => isoYearOfDate(h.date),
  week: (h) => h.week,
  farm: (h) => h.farm_name || summarizeHarvestSales(h.sales_detail || []).farm,
  exporters: (h) => (h.sales_detail || []).map((s) => s.exporter_name),
};

export default function HarvestHistoryPage() {
  const { user } = useAuth();
  const canDelete = hasNavPermission(user, 'delete_records');
  const [harvests, setHarvests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [status, setStatus] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [preview, setPreview] = useState(null); // harvest being previewed | null
  useEffect(() => {
    api.get('harvest-details/')
      .then((r) => setHarvests(r.data))
      .catch(() => setStatus({ type: 'error', text: 'No se pudo cargar el historial de cosecha.' }))
      .finally(() => setLoading(false));
  }, []);

  const { filtered, bar: filterBar } = useHistoryFilters(harvests, FILTER_FIELDS);
  const sum = (field) => filtered.reduce((total, h) => total + (h[field] || 0), 0);
  const summary = [
    ['Racimos cosechados', sum('bunches_harvested')],
    ['Racimos rechazados', sum('bunches_rejected')],
    ['Racimos procesados', sum('bunches_processed')],
  ];

  const handleDelete = async (id) => {
    if (!window.confirm('¿Eliminar estos detalles de cosecha?')) return;
    setDeletingId(id);
    setStatus(null);
    try {
      await api.delete(`harvest-details/${id}/`);
      setHarvests((xs) => xs.filter((x) => x.id !== id));
    } catch (err) {
      setStatus({ type: 'error', text: apiErrorText(err, 'No se pudieron eliminar los detalles de cosecha.') });
    } finally {
      setDeletingId(null);
    }
  };

  return (
    <div className="companies-root">
      <div className="sales-form-topbar">
        <Link to="/production" className="topbar-btn">← Producción</Link>
        <Link to="/cosecha" className="topbar-btn topbar-btn-primary">+ Nuevos detalles de cosecha</Link>
      </div>
      <h1 className="companies-title">📋 Historial de Cosecha</h1>
      <p className="page-subtitle page-header-divided">Los detalles de cosecha de cada venta. Ábrelos para seguir editando, o elimínalos desde aquí.</p>

      {status && <div className={`companies-status companies-status-${status.type}`}>{status.text}</div>}

      {filterBar}

      <HistorySummary cards={summary} />

      <div className="companies-card">
        {loading ? (
          <div className="companies-empty">Cargando…</div>
        ) : harvests.length === 0 ? (
          <div className="companies-empty">Aún no hay detalles de cosecha registrados.</div>
        ) : filtered.length === 0 ? (
          <div className="companies-empty">Sin detalles de cosecha para los filtros seleccionados.</div>
        ) : (
          <table className="companies-table sales-history-table">
            <thead>
              <tr>
                <th className="sales-id-col">ID</th>
                <th>Fecha</th>
                <th>Hacienda</th>
                <th>Exportadora</th>
                <th className="num">Racimos cosechados</th>
                <th className="num">Racimos rechazados</th>
                <th className="num">Racimos procesados</th>
                <th className="num">Ratio</th>
                <th className="num">Cajas</th>
                <th>Cintas</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {filtered.map((h) => {
                const s = summarizeHarvestSales(h.sales_detail || []);
                return (
                  <tr key={h.id} title={h.created_by_username ? `Registrado por ${h.created_by_username}` : undefined}>
                    <td className="sales-id-col sales-id">{h.id}</td>
                    <td>
                      <div>{formatISODate(h.date)}</div>
                      <div className="sales-sub">Sem {h.week} · {h.day}</div>
                    </td>
                    <td>
                      <div>{h.farm_name || s.farm}</div>
                      <div className="sales-sub">{s.ids.length > 1 ? 'Ventas' : 'Venta'} {s.ids.map((id) => `#${id}`).join(', ')}</div>
                    </td>
                    <td>{s.exporters}</td>
                    <td className="num">{h.bunches_harvested.toLocaleString('es-EC')}</td>
                    <td className="num">{h.bunches_rejected.toLocaleString('es-EC')}</td>
                    <td className="num"><strong>{h.bunches_processed.toLocaleString('es-EC')}</strong></td>
                    <td className="num">{formatRatio(h.ratio)}</td>
                    <td className="num">
                      {s.boxes.map((b) => (
                        <div key={b.saleId}>
                          {s.boxes.length > 1 && <span className="sales-sub">#{b.saleId}: </span>}
                          {b.value?.toLocaleString('es-EC')}
                          <div className="sales-sub">(43lbs): {formatBoxes43(b.value43)}</div>
                        </div>
                      ))}
                    </td>
                    <td>
                      {h.ribbons.length === 0 ? '—' : h.ribbons.map((rb) => (
                        <div key={rb.id} className="harvest-history-ribbon">
                          <span className="harvest-swatch" style={{ background: rb.ribbon_hex_color || 'transparent' }} />
                          {rb.ribbon_color} · {rb.quantity.toLocaleString('es-EC')}
                        </div>
                      ))}
                    </td>
                    <td>
                      <div className="row-actions">
                        <Link className="row-icon-btn" to={`/cosecha/${h.id}`} title="Editar" aria-label="Editar">✏️</Link>
                        <button
                          className="row-icon-btn"
                          onClick={() => setPreview(h)}
                          title="Exportar (Excel o PDF)"
                          aria-label="Exportar"
                        >
                          📤
                        </button>
                        <RowProperties item={h} title={`Cosecha #${h.id} · ${h.farm_name || ''}`} />
                        {canDelete && (
                          <button
                            className="row-icon-btn"
                            onClick={() => handleDelete(h.id)}
                            disabled={deletingId === h.id}
                            title="Eliminar"
                            aria-label="Eliminar"
                          >
                            🗑️
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {preview && (
        <ExportPreviewModal
          title={`Cosecha #${preview.id} · ${preview.farm_name || preview.sales_detail?.[0]?.farm_name || ''}`}
          fileName={harvestSheet(preview).fileBase}
          onClose={() => setPreview(null)}
          onDownload={(fmt) => (fmt === 'excel' ? exportHarvestToExcel(preview) : exportHarvestToPDF(preview))}
        >
          <SheetPreview sheet={harvestSheet(preview)} />
        </ExportPreviewModal>
      )}
    </div>
  );
}
